import Phaser from "phaser";
import { makeButton } from "../ui/widgets";
import { Palette } from "../ui/palette";
import { GameState } from "../state/GameState";

/**
 * Rolling credits on the Mallorca sunset, shown after the ending video.
 *
 * Three layers make the credits rise "out of the horizon":
 *   depth 0  credits-bg.png        - the full sunset scene (back)
 *   depth 1  the scrolling credits - text tweened bottom -> top (middle)
 *   depth 2  credits-bg-front.png  - the same scene with a TRANSPARENT sky (front)
 * The foreground's opaque landscape hides any credit line below the horizon, so a
 * line only becomes visible as it climbs into the sky - i.e. it emerges from the
 * horizon and floats up.
 *
 * The credit text comes from an editable markdown file (public/credits.md) so it can
 * be changed without touching code. When the roll finishes (or Skip is pressed) the
 * flow continues to the score-submission screen.
 */

const SCROLL_SPEED = 55; // px per second

/** Fallback text if credits.md can't be loaded, so the scene never breaks. */
const FALLBACK_MD = `# Code & Co\n\n## Game Design\nMatthew Kaliara\n\n## And You\nThanks for playing!`;

type Token =
  | { kind: "title"; text: string }
  | { kind: "role"; text: string }
  | { kind: "name"; text: string }
  | { kind: "gap"; size: number };

/** Parse the credits markdown into styled tokens.
 *  `# X` -> title, `## X` -> role/section header, other text -> a name,
 *  blank line -> extra vertical gap. */
function parseCredits(md: string): Token[] {
  const out: Token[] = [];
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      out.push({ kind: "gap", size: 20 });
    } else if (line.startsWith("## ")) {
      out.push({ kind: "role", text: line.slice(3).trim() });
    } else if (line.startsWith("# ")) {
      out.push({ kind: "title", text: line.slice(2).trim() });
    } else {
      out.push({ kind: "name", text: line });
    }
  }
  return out;
}

export class CreditsScene extends Phaser.Scene {
  private done = false;

  constructor() {
    super("Credits");
  }

  preload() {
    this.load.image("credits-bg", "assets/credits-bg.png");
    this.load.image("credits-bg-front", "assets/credits-bg-front.png");
    this.load.image("credits-winners", "assets/credits-winners.png");
    this.load.image("credits-winners-sheepy", "assets/credits-winner-with-sheepy.png");
    this.load.text("credits-md", "credits.md");
  }

  create() {
    const { width, height } = this.scale;
    this.done = false;

    // Back layer: the full sunset scene.
    const bg = this.add.image(width / 2, height / 2, "credits-bg").setDepth(0);
    this.coverFit(bg);

    // Middle layer: the scrolling credits.
    const tokens = parseCredits((this.cache.text.get("credits-md") as string) || FALLBACK_MD);
    const credits = this.add.container(0, 0).setDepth(1);
    const contentHeight = this.layoutCredits(credits, tokens, width);

    // Front layer: same scene with a transparent sky, so lines below the horizon are
    // hidden behind the landscape and only appear once they rise into the sky.
    const front = this.add.image(width / 2, height / 2, "credits-bg-front").setDepth(2);
    this.coverFit(front);

    // Winners layer: the founders on their beach chairs, on top of everything so they
    // stay visible. Same-size art as the backgrounds, so it just cover-fits and lines
    // up. If every Sheepy was collected, use the variant that also shows Sheepy.
    const winnersKey = GameState.allSheepies() ? "credits-winners-sheepy" : "credits-winners";
    const winners = this.add.image(width / 2, height / 2, winnersKey).setDepth(3);
    this.coverFit(winners);

    // Skip -> straight to score submission (kept above every layer).
    makeButton(this, width - 74, height - 28, "Skip  ▶", () => this.finish(), {
      width: 120,
      height: 36,
      fontSize: "14px",
      fill: Palette.panel,
    }).setDepth(4);
    this.input.keyboard!.on("keydown-ESC", () => this.finish());

    // Roll the credits up until the last line has climbed off the top. Start just
    // below the horizon (not the very bottom) so the first credits emerge and start
    // rolling sooner, rather than after a long climb from off-screen.
    this.cameras.main.fadeIn(700, 0, 0, 0);
    const startY = height * 0.55;
    credits.y = startY;
    const distance = startY + contentHeight;
    this.tweens.add({
      targets: credits,
      y: -contentHeight,
      duration: (distance / SCROLL_SPEED) * 1000,
      onComplete: () => this.finish(),
    });
  }

  /** Lay the tokens out top-to-bottom inside the container, centered. Returns the
   *  total content height. */
  private layoutCredits(container: Phaser.GameObjects.Container, tokens: Token[], width: number): number {
    let y = 0;
    for (const tok of tokens) {
      if (tok.kind === "gap") {
        y += tok.size;
        continue;
      }
      if (tok.kind === "role") y += 22; // breathing room before a new section
      const t = this.add.text(width / 2, y, tok.text, this.styleFor(tok.kind)).setOrigin(0.5, 0);
      container.add(t);
      y += t.height + (tok.kind === "title" ? 30 : tok.kind === "role" ? 10 : 6);
    }
    return y;
  }

  private styleFor(kind: "title" | "role" | "name"): Phaser.Types.GameObjects.Text.TextStyle {
    const base = { fontFamily: "monospace", stroke: "#000000", align: "center" as const };
    if (kind === "title") return { ...base, fontSize: "40px", color: "#ffffff", strokeThickness: 6 };
    if (kind === "role") return { ...base, fontSize: "22px", color: "#ffd27f", strokeThickness: 5 };
    return { ...base, fontSize: "18px", color: "#ffffff", strokeThickness: 4 };
  }

  /** Scale an image so it covers the whole canvas (no letterboxing), centered. */
  private coverFit(img: Phaser.GameObjects.Image) {
    const { width, height } = this.scale;
    img.setScale(Math.max(width / img.width, height / img.height));
  }

  private finish() {
    if (this.done) return;
    this.done = true;
    this.cameras.main.fadeOut(700, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () =>
      this.scene.start("SubmitScore"),
    );
  }
}
